import React, { useState, useRef, useEffect } from 'react';
import menu from '../assets/images/icon/menu.png';
import search from '../assets/images/icon/search-normal.png';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useApp from 'hooks/useApp';
import { CHANGE_STATE } from 'reducers/app.reducer';
import NotificationBell from './NotificationBell';
import CompanySwitcher from './CompanySwitcher';
import useBranding from 'hooks/useBranding';
import { clearAuthKeepDomainBranding } from 'utils/domainBranding';

const BREADCRUMB_LABELS = {
  // Dashboard
  'dashboard': 'Dashboard',
  'manage-company': 'Manage Company',
  'start-new-payroll': 'Run Payroll',
  'payroll-time-keeping': 'Time Keeping',
  'add-time-card': 'Add Time Card',
  'locations': 'Locations',
  'billing': 'Billing',
  'my-companies': 'My Companies',
  'account-settings': 'Account Settings',
  'tax-brackets': 'Tax Brackets',
  'payroll': 'Payroll Detail',
  // Employees
  'employees': 'Employees',
  'add-edit-employee': 'Add / Edit Employee',
  'view': 'View',
  // Contractor
  'contractor': 'Contractor',
  'add-edit-contractor': 'Add / Edit Contractor',
  // Benefits
  'benefits': 'Benefits',
  'add-edit-company-benefit': 'Add / Edit Benefit',
  'add-edit-employee-benefit': 'Add / Edit Employee Benefit',
  // Deductions
  'deductions': 'Deductions',
  'add-edit-mcn-deduction-benefit': 'Add / Edit Deduction',
  'add-edit-child-support-deduction': 'Add / Edit Child Support',
  'add-edit-garnishment-deduction': 'Add / Edit Garnishment',
  // Time Off & Misc
  'time-off': 'Time Off',
  'onboarding': 'Onboarding',
  'pending': 'Pending',
  'notifications': 'Notifications',
  // Reports
  'reports': 'Reports',
  'all-reports': 'All Reports',
  'authorization-docs': 'Authorization Docs',
  'company-tax-docs': 'Company Tax Docs',
  'employee-tax-docs': 'Employee Tax Docs',
  'contractor-tax-docs': 'Contractor Tax Docs',
  'audit-trail': 'Audit Trail',
  // Employee Portal
  'employee': 'Employee Portal',
  'profile': 'Profile',
  'clock-time': 'Clock Time',
  'paystubs': 'Pay Stubs',
  'documents': 'Documents',
  'account': 'Account',
  'bank-account': 'Bank Account',
  // Contractor Portal
  'contractor-portal': 'Contractor Portal',
  'payments': 'Payments',
};

const isUuid = (str) => /^[0-9a-f-]{20,}$/i.test(str);

const formatSegment = (segment) => {
  if (isUuid(segment)) return null; // skip UUIDs
  return BREADCRUMB_LABELS[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getDisplayName = () => {
  const stored = localStorage.getItem("userName");
  if (stored) return stored;
  const email = localStorage.getItem("userEmail") || "";
  const part = email.split("@")[0] || "User";
  return part.charAt(0).toUpperCase() + part.slice(1).replace(/[._-]/g, ' ');
};

const getInitials = (name) => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const Header = ({ onMenuClick }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pathnames = pathname.split("/").filter((x) => x);
  const { isSidebarOpen, dispatch } = useApp();
  const { productName } = useBranding();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const displayName = getDisplayName();
  const userEmail = localStorage.getItem("userEmail") || "";
  const userRole = localStorage.getItem("userRole") || "";
  const homeLink = userRole === "Employee" ? "/employee" : userRole === "Contractor" ? "/contractor-portal" : "/dashboard";

  // Notification recipient based on role
  const notifRecipientId = userRole === "Employee"
    ? localStorage.getItem("employeeId")
    : userRole === "Contractor"
    ? localStorage.getItem("contractorId")
    : localStorage.getItem("companyId");
  const notifRecipientType = userRole === "Employee" ? "employee"
    : userRole === "Contractor" ? "contractor" : "company";

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleLogout = async () => {
    const fromReseller = window.location.pathname.startsWith("/reseller")
      || localStorage.getItem("userRole") === "Reseller";
    const onTenantHost = (() => {
      const host = window.location.hostname.toLowerCase();
      return host.endsWith(".payrollnoot.com") && host !== "www.payrollnoot.com";
    })();
    await clearAuthKeepDomainBranding();
    if (fromReseller) navigate("/login/reseller");
    else if (onTenantHost) navigate("/login/company");
    else navigate("/login");
  };

  return (
    <div className="header" style={{ borderBottom: pathname !== '/' ? '1px solid #C9C9C9' : "" }}>
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-4">
          <img
            src={menu}
            alt="Menu"
            onClick={() => {
              if (onMenuClick) {
                onMenuClick();
              } else {
                dispatch({ type: CHANGE_STATE, payload: { isSidebarOpen: !isSidebarOpen } });
              }
            }}
          />
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#051147' }}>{productName}</h2>
            {pathname !== '/' && (
              <div className="breadcrumb-container">
                <Link to={homeLink} className="breadcrumb-link">Home</Link>
                {pathnames.map((value, index) => {
                  const label = formatSegment(value);
                  if (!label) return null;
                  const to = `/${pathnames.slice(0, index + 1).join("/")}`;
                  const isLast = index === pathnames.length - 1 || pathnames.slice(index + 1).every(isUuid);
                  return (
                    <span key={to}>
                      {" > "}
                      {isLast ? (
                        <span className="breadcrumb-link" style={{ color: '#1B75F0', fontWeight: 600 }}>{label}</span>
                      ) : (
                        <Link to={to} className="breadcrumb-link">{label}</Link>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          {pathname === '/' && (
            <form>
              <div className="search-input">
                <input type='text' />
                <img className="search-icon" src={search} alt="Search" />
              </div>
            </form>
          )}
        </div>

        <div className="d-flex align-items-center gap-4">
          <NotificationBell recipientId={notifRecipientId} recipientType={notifRecipientType} />

          {/* Company Switcher — Company, Accountant, and SuperAdmin */}
          {(userRole === 'Company' || userRole === 'Accountant' || userRole === 'SuperAdmin' || userRole === 'Reseller') && <CompanySwitcher />}
          {userRole === 'Reseller' && (
            <a href="/reseller" style={{
              fontSize: 13, fontWeight: 600, color: 'var(--brand-primary, #1B75F0)',
              textDecoration: 'none', marginRight: 8,
            }}>← Reseller Portal</a>
          )}

          {/* User Menu */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setDropdownOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'linear-gradient(135deg, #1B75F0, #5B9BF5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0,
              }}>
                {getInitials(displayName)}
              </div>
              <div className="header-user-name" style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayName}
                </div>
                <div style={{ fontSize: 11, color: '#999' }}>{userRole}</div>
              </div>
              <svg viewBox="0 0 24 24" fill="none" width="12" height="12" style={{ color: '#999', flexShrink: 0 }}>
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {/* Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                minWidth: 200, zIndex: 1000, overflow: 'hidden',
              }}>
                {/* User info header */}
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{displayName}</div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{userEmail}</div>
                  <div style={{ fontSize: 11, color: '#1B75F0', marginTop: 4, fontWeight: 500 }}>{userRole}</div>
                </div>

                {/* Account Settings — Company admin only */}
                {userRole === 'Company' && (
                  <button
                    onClick={() => { setDropdownOpen(false); navigate('/dashboard/account-settings'); }}
                    style={{
                      width: '100%', padding: '12px 16px', background: 'none',
                      border: 'none', borderBottom: '1px solid #f0f0f0', textAlign: 'left', cursor: 'pointer',
                      fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 10,
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                      <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Account Settings
                  </button>
                )}

                {/* Logout option */}
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%', padding: '12px 16px', background: 'none',
                    border: 'none', textAlign: 'left', cursor: 'pointer',
                    fontSize: 13, color: '#dc3545', display: 'flex', alignItems: 'center', gap: 10,
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
